import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaMoneyBillWave,
  FaFileSignature,
  FaUpload,
  FaCalendarDays,
  FaXmark,
  FaClipboardCheck,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";
const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
      "currentDesignation",
      "currentDepartment",
      "currentReportingManager",
    ],
  },
  {
    id: 3,
    title: "Promotion\nDetails",
    mobileTitle: "Promotion\nDetails",
    icon: FaBriefcase,
    fields: [
      "promotionEffectiveDate",
      "currentDesignation",
      "newDesignation",
      "currentDepartment",
      "newDepartment",
      "newReportingManager",
      "newWorkLocation",
      "promotionReason",
    ],
  },
  {
    id: 4,
    title: "Salary &\nBenefits",
    mobileTitle: "Salary &\nBenefits",
    icon: FaMoneyBillWave,
    fields: [
      "currentSalary",
      "revisedSalary",
      "currentMonthlySalary",
      "revisedMonthlySalary",
      "salaryEffectiveDate",
      "revisedBonusOrIncentive",
      "additionalBenefits",
      "salaryRevisionType",
    ],
  },
  {
    id: 5,
    title: "New Role\nInformation",
    mobileTitle: "New Role\nInformation",
    icon: FaClipboardCheck,
    fields: [
      "newJobResponsibilities",
      "performanceExpectations",
      "newRoleDescription",
      "newRoleDepartment",
      "newRoleReportingManager",
    ],
  },
  {
    id: 6,
    title: "Letter\nInformation",
    mobileTitle: "Letter\nInformation",
    icon: FaFileSignature,
    fields: [
      "promotionLetterDate",
      "placeOfIssue",
      "referenceNumber",
      "signatoryName",
      "signatoryDesignation",
      "additionalRemarks",
    ],
  },
];

const defaultValues = {
  // ================= COMPANY =================
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // ================= EMPLOYEE =================
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",
  currentDesignation: "",
  currentDepartment: "",
  currentReportingManager: "",

  // ================= PROMOTION =================
  promotionEffectiveDate: "",
  newDesignation: "",
  newDepartment: "",
  newReportingManager: "",
  newWorkLocation: "",
  promotionReason: "",

  // ================= SALARY =================
  currentSalary: "",
  revisedSalary: "",
  currentMonthlySalary: "",
  revisedMonthlySalary: "",
  salaryEffectiveDate: "",
  revisedBonusOrIncentive: "",
  additionalBenefits: "",
  salaryRevisionType: "",

  // ================= NEW ROLE =================
  newJobResponsibilities: "",
  performanceExpectations: "",
  newRoleDescription: "",
  newRoleDepartment: "",
  newRoleReportingManager: "",

  // ================= LETTER =================
  promotionLetterDate: "",
  placeOfIssue: "",
  referenceNumber: "",
  signatoryName: "",
  signatoryDesignation: "",
  additionalRemarks: "",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};
const BASE_URL = import.meta.env.VITE_BASE_URL;

const PromotionLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");

    // Revalidate the field
    //  await trigger("companyLogo");
  };

  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append("companyLogo", value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-promotionletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return an promotion Letter PDF");
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Promotion-Letter-${data.employeeName || "Employee"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.open(url, "_blank");

      reset(defaultValues);

      setLogoPreview("");

      setActiveStep(0);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("promotion letter generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          alert(errorData?.message || "Failed to generate promotion Letter");
        } catch {
          alert("Failed to generate promotion Letter");
        }
      } else {
        alert(error?.message || "Failed to generate promotion Letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };
  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  return (
    <section>
      <form className="w-full">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
          {/* ================= FORM ================= bg-[#eef4f8]*/}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-4 py-2.5 text-center transition
          sm:min-w-20 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70"
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <PromotionEmployeeInformation
                  register={register}
                  errors={errors}
                />
              )}

              {activeStep === 2 && (
                <PromotionDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <SalaryBenefits
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 4 && (
                <NewRoleInformation register={register} errors={errors} />
              )}

              {activeStep === 5 && (
                <PromotionLetterInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}
            </div>
            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Promotion Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <PromotionLetterPreview values={values} logoPreview={logoPreview} />
        </div>
      </form>
    </section>
  );
};

export default PromotionLetterGeneratorForm;

const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>
      </div>
    </div>
  );
};

const PromotionEmployeeInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employee Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Employee Full Name"
          required
          error={errors.employeeName}
        >
          <input
            {...register("employeeName", {
              required: "Employee name is required",
            })}
            className={inputClass}
            placeholder="Enter employee name"
          />
        </FormField>

        <FormField label="Employee ID" required error={errors.employeeId}>
          <input
            {...register("employeeId", {
              required: "Employee ID is required",
            })}
            className={inputClass}
            placeholder="Enter employee ID"
          />
        </FormField>

        <FormField label="Phone Number" error={errors.employeePhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("employeePhone")}
            className={inputClass}
            placeholder="Enter phone number"
          />
        </FormField>

        <FormField label="Email Address" required error={errors.employeeEmail}>
          <input
            type="email"
            {...register("employeeEmail", {
              required: "Employee email is required",
            })}
            className={inputClass}
            placeholder="Enter email address"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Residential Address"
            required
            error={errors.residentialAddress}
          >
            <input
              {...register("residentialAddress", {
                required: "Residential address is required",
              })}
              className={inputClass}
              placeholder="Enter address"
            />
          </FormField>
        </div>

        <FormField
          label="Current Designation"
          required
          error={errors.currentDesignation}
        >
          <input
            {...register("currentDesignation", {
              required: "Current designation is required",
            })}
            className={inputClass}
            placeholder="Enter current designation"
          />
        </FormField>

        <FormField label="Current Department" error={errors.currentDepartment}>
          <input
            {...register("currentDepartment")}
            className={inputClass}
            placeholder="Enter current department"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Current Reporting Manager"
            error={errors.currentReportingManager}
          >
            <input
              {...register("currentReportingManager")}
              className={inputClass}
              placeholder="Enter reporting manager"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const PromotionDetails = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Promotion Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Promotion effective date"
          required
          error={errors.promotionEffectiveDate}
        >
          <Controller
            name="promotionEffectiveDate"
            control={control}
            rules={{
              required: "Promotion effective date is required",
            }}
            render={({ field }) => (
              <DatePicker
                selected={parseDateValue(field.value)}
                onChange={(date) => field.onChange(formatDateValue(date))}
                onBlur={field.onBlur}
                dateFormat="yyyy-MM-dd"
                isClearable
                placeholderText="Select effective date"
                showIcon
                toggleCalendarOnIconClick
                icon={<CalendarIcon className="h-4 w-4" />}
                className={`${inputClass} h-10`}
                wrapperClassName="w-full"
                popperClassName="payslip-datepicker-popper react-popper-offset"
                calendarClassName="payslip-datepicker"
                popperPlacement="top-start"
                portalId="datepicker-portal"
              />
            )}
          />
        </FormField>

        <FormField
          label="New designation"
          required
          error={errors.newDesignation}
        >
          <input
            {...register("newDesignation", {
              required: "New designation is required",
            })}
            className={inputClass}
            placeholder="Enter new designation"
          />
        </FormField>

        <FormField label="New department" error={errors.newDepartment}>
          <input
            {...register("newDepartment")}
            className={inputClass}
            placeholder="Enter new department"
          />
        </FormField>

        <FormField
          label="New reporting manager"
          error={errors.newReportingManager}
        >
          <input
            {...register("newReportingManager")}
            className={inputClass}
            placeholder="Enter reporting manager"
          />
        </FormField>

        <FormField label="New work location" error={errors.newWorkLocation}>
          <input
            {...register("newWorkLocation")}
            className={inputClass}
            placeholder="Enter work location"
          />
        </FormField>

        <FormField label="Reason for promotion" error={errors.promotionReason}>
          <select {...register("promotionReason")} className={inputClass}>
            <option value="">Select reason</option>
            <option value="Performance">Performance</option>
            <option value="Career Growth">Career Growth</option>
            <option value="Role Expansion">Role Expansion</option>
            <option value="Internal Movement">Internal Movement</option>
            <option value="Business Requirement">Business Requirement</option>
            <option value="Other">Other</option>
          </select>
        </FormField>
      </div>
    </div>
  );
};

const SalaryBenefits = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Salary and Benefits
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Current annual CTC" error={errors.currentSalary}>
          <input
            type="number"
            {...register("currentSalary")}
            className={inputClass}
            placeholder="Enter current CTC"
          />
        </FormField>

        <FormField
          label="Revised annual CTC"
          required
          error={errors.revisedSalary}
        >
          <input
            type="number"
            {...register("revisedSalary", {
              required: "Revised CTC is required",
            })}
            className={inputClass}
            placeholder="Enter revised CTC"
          />
        </FormField>

        <FormField
          label="Current monthly gross salary"
          error={errors.currentMonthlySalary}
        >
          <input
            type="number"
            {...register("currentMonthlySalary")}
            className={inputClass}
            placeholder="Enter current monthly salary"
          />
        </FormField>

        <FormField
          label="Revised monthly gross salary"
          error={errors.revisedMonthlySalary}
        >
          <input
            type="number"
            {...register("revisedMonthlySalary")}
            className={inputClass}
            placeholder="Enter revised monthly salary"
          />
        </FormField>

        <FormField
          label="Salary revision effective date"
          error={errors.salaryEffectiveDate}
        >
          <Controller
            name="salaryEffectiveDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                selected={parseDateValue(field.value)}
                onChange={(date) => field.onChange(formatDateValue(date))}
                dateFormat="yyyy-MM-dd"
                isClearable
                placeholderText="Select date"
                showIcon
                toggleCalendarOnIconClick
                icon={<CalendarIcon className="h-4 w-4" />}
                className={`${inputClass} h-10`}
                wrapperClassName="w-full"
                popperClassName="payslip-datepicker-popper react-popper-offset"
                calendarClassName="payslip-datepicker"
                popperPlacement="top-start"
                portalId="datepicker-portal"
              />
            )}
          />
        </FormField>

        <FormField
          label="Revised bonus / incentive"
          error={errors.revisedBonusOrIncentive}
        >
          <input
            {...register("revisedBonusOrIncentive")}
            className={inputClass}
            placeholder="Enter bonus or incentive"
          />
        </FormField>

        <FormField
          label="Additional benefits"
          error={errors.additionalBenefits}
        >
          <input
            {...register("additionalBenefits")}
            className={inputClass}
            placeholder="Enter additional benefits"
          />
        </FormField>

        <FormField
          label="Salary revision type"
          error={errors.salaryRevisionType}
        >
          <select {...register("salaryRevisionType")} className={inputClass}>
            <option value="">Select type</option>
            <option value="Annual Revision">Annual Revision</option>
            <option value="Promotion Revision">Promotion Revision</option>
            <option value="Performance Revision">Performance Revision</option>
            <option value="Other">Other</option>
          </select>
        </FormField>
      </div>
    </div>
  );
};

const NewRoleInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        New Role Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FormField
            label="New job responsibilities"
            required
            error={errors.newJobResponsibilities}
          >
            <textarea
              {...register("newJobResponsibilities", {
                required: "Job responsibilities are required",
              })}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter new job responsibilities"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField
            label="Performance expectations"
            error={errors.performanceExpectations}
          >
            <textarea
              {...register("performanceExpectations")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter performance expectations"
            />
          </FormField>
        </div>

        <FormField label="New role department" error={errors.newRoleDepartment}>
          <input
            {...register("newRoleDepartment")}
            className={inputClass}
            placeholder="Enter department"
          />
        </FormField>

        <FormField
          label="New role reporting manager"
          error={errors.newRoleReportingManager}
        >
          <input
            {...register("newRoleReportingManager")}
            className={inputClass}
            placeholder="Enter reporting manager"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="New role description"
            error={errors.newRoleDescription}
          >
            <textarea
              {...register("newRoleDescription")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter new role description"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const PromotionLetterInformation = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Letter Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Promotion letter date"
          required
          error={errors.promotionLetterDate}
        >
          <Controller
            name="promotionLetterDate"
            control={control}
            rules={{
              required: "Promotion letter date is required",
            }}
            render={({ field }) => (
              <DatePicker
                selected={parseDateValue(field.value)}
                onChange={(date) => field.onChange(formatDateValue(date))}
                onBlur={field.onBlur}
                dateFormat="yyyy-MM-dd"
                isClearable
                placeholderText="Select letter date"
                showIcon
                toggleCalendarOnIconClick
                icon={<CalendarIcon className="h-4 w-4" />}
                className={`${inputClass} h-10`}
                wrapperClassName="w-full"
                popperClassName="payslip-datepicker-popper react-popper-offset"
                calendarClassName="payslip-datepicker"
                popperPlacement="top-start"
                portalId="datepicker-portal"
              />
            )}
          />
        </FormField>

        <FormField label="Place of issue" error={errors.placeOfIssue}>
          <input
            {...register("placeOfIssue")}
            className={inputClass}
            placeholder="Enter place of issue"
          />
        </FormField>

        <FormField label="Reference number" error={errors.referenceNumber}>
          <input
            {...register("referenceNumber")}
            className={inputClass}
            placeholder="Enter reference number"
          />
        </FormField>

        <FormField
          label="Authorised signatory name"
          required
          error={errors.signatoryName}
        >
          <input
            {...register("signatoryName", {
              required: "Signatory name is required",
            })}
            className={inputClass}
            placeholder="Enter signatory name"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Authorised signatory designation"
            required
            error={errors.signatoryDesignation}
          >
            <input
              {...register("signatoryDesignation", {
                required: "Designation is required",
              })}
              className={inputClass}
              placeholder="Enter signatory designation"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField
            label="Additional message or remarks"
            error={errors.additionalRemarks}
          >
            <textarea
              {...register("additionalRemarks")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter additional message or remarks"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const PromotionLetterPreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white p-4 shadow-sm backdrop-blur-2xl">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="w-full bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* =========================
              COMPANY HEADER
          ========================= */}
          <div className="flex items-start gap-3 border-b border-gray-200 pb-2">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-17.5 max-w-[40%] min-w-17.5 object-contain"
              />
            ) : null}

            <div className="min-w-0 flex-1">
              <p className="text-lg font-bold leading-6 text-black">
                {values.companyName || "Company"}
              </p>

              {values.companyAddress && (
                <p className="mt-1 text-xs leading-5 text-black">
                  <strong>Address:</strong> {values.companyAddress}
                </p>
              )}

              {(values.companyEmail ||
                values.companyPhone ||
                values.companyWebsite) && (
                <p className="text-xs leading-5 text-black">
                  {values.companyEmail && (
                    <>
                      <strong>Email:</strong> {values.companyEmail}
                    </>
                  )}

                  {values.companyPhone && (
                    <>
                      &nbsp;&nbsp;
                      <strong>Phone:</strong> {values.companyPhone}
                    </>
                  )}

                  {values.companyWebsite && (
                    <>
                      &nbsp;&nbsp;
                      <strong>Website:</strong> {values.companyWebsite}
                    </>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* =========================
              TITLE
          ========================= */}
          <div className="mt-5 text-center">
            <p className="text-sm font-bold text-[#10324F]">PROMOTION LETTER</p>

            <p className="mt-1 text-right text-xs text-[#10324F]">
              {formatDate(values.promotionLetterDate)}
            </p>
          </div>

          {/* =========================
              REFERENCE
          ========================= */}
          {values.referenceNumber && (
            <div className="mt-3 text-xs text-black">
              <strong>Reference No:</strong> {values.referenceNumber}
            </div>
          )}

          {/* =========================
              RECIPIENT
          ========================= */}
          <div className="mt-6 text-xs leading-5 text-black">
            <p className="font-bold">To:</p>

            <p>{values.employeeName || "Employee Name"}</p>

            <p>{values.residentialAddress || "Employee Address"}</p>
          </div>

          {/* =========================
              LETTER BODY
          ========================= */}
          <div className="mt-6 text-xs leading-5 text-black">
            {/* Greeting */}
            <p>Dear {values.employeeName || "Employee"},</p>

            {/* Paragraph 1 */}
            <p className="mt-3 text-justify">
              We are pleased to inform you that, in recognition of your
              performance, contribution and commitment to{" "}
              <strong>{values.companyName || "Company Name"}</strong>, you have
              been promoted from{" "}
              <strong>
                {values.currentDesignation || "Current Designation"}
              </strong>{" "}
              to <strong>{values.newDesignation || "New Designation"}</strong>.
            </p>

            {/* Paragraph 2 - Combined promotion details */}
            <p className="mt-3 text-justify">
              {values.promotionEffectiveDate && (
                <>
                  Your promotion will be effective from{" "}
                  <strong>{formatDate(values.promotionEffectiveDate)}</strong>
                  .{" "}
                </>
              )}

              {values.newDepartment && (
                <>
                  You will be associated with the{" "}
                  <strong>{values.newDepartment}</strong> department in your new
                  role.{" "}
                </>
              )}

              {values.newReportingManager && (
                <>
                  You will report to{" "}
                  <strong>{values.newReportingManager}</strong>.{" "}
                </>
              )}

              {values.newWorkLocation && (
                <>
                  Your new work location will be{" "}
                  <strong>{values.newWorkLocation}</strong>.{" "}
                </>
              )}
            </p>

            {/* Paragraph 3 - Combined salary details */}
            {values.revisedSalary && (
              <p className="mt-3 text-justify">
                Your revised annual compensation will be{" "}
                <strong>{values.revisedSalary}</strong>
                {values.salaryEffectiveDate && (
                  <>
                    , effective from{" "}
                    <strong>{formatDate(values.salaryEffectiveDate)}</strong>
                  </>
                )}
                .
              </p>
            )}

            {/* Paragraph 4 - Combined responsibilities + expectations + remarks */}
            {(values.newJobResponsibilities ||
              values.performanceExpectations ||
              values.newRoleDescription ||
              values.additionalRemarks) && (
              <p className="mt-3 text-justify">
                {values.newJobResponsibilities && (
                  <>
                    In your new role, your responsibilities will include{" "}
                    {values.newJobResponsibilities}.{" "}
                  </>
                )}

                {values.performanceExpectations && (
                  <>
                    We expect you to continue demonstrating strong performance
                    and meet the expectations associated with your new position.{" "}
                    {values.performanceExpectations}{" "}
                  </>
                )}

                {values.newRoleDescription && <>{values.newRoleDescription} </>}

                {values.additionalRemarks && <>{values.additionalRemarks}</>}
              </p>
            )}

            {/* Final paragraph */}
            <p className="mt-3 text-justify">
              We congratulate you on your promotion and wish you continued
              success in your new role.
            </p>
          </div>

          {/* =========================
              PLACE / DATE
          ========================= */}
          <div className="mt-6 text-xs leading-5 text-black">
            <p>
              <strong>Place of Issue:</strong>{" "}
              {values.placeOfIssue || "____________"}
            </p>

            <p>
              <strong>Date:</strong> {formatDate(values.promotionLetterDate)}
            </p>
          </div>

          {/* =========================
              SIGNATURE
          ========================= */}
          <div className="mt-7 text-xs text-black">
            <p>
              <strong className="text-sm">Sincerely,</strong>
            </p>

            <p className="mt-3">
              {values.signatoryName || "Authorised Signatory"}
            </p>

            <p>{values.signatoryDesignation || "HR Manager"}</p>

            <p>{values.companyName || "Company Name"}</p>
          </div>

          {/* =========================
              POWERED BY
          ========================= */}
          <div className="mt-8 text-center text-xs text-[#5b697e]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
